import React, { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const initialForm = {
  nom: '',
  email: '',
  telephone: '',
  type_bateau: '',
  zone: '',
  message: '',
  website: '', // honeypot
};

const QuoteRequestModal = ({ open, onClose, service }) => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setSuccess(false);
      setError(null);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !service) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.email.trim()) {
      setError("Le nom et l'email sont obligatoires.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/quote-request/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service: service.key,
          service_label: service.label,
          ...form,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur lors de l\'envoi');
      }
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="quote-modal-backdrop"
    >
      <div
        className="relative bg-[#1a1c1b] border-2 border-[#d9fb06]/50 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="quote-modal"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="absolute top-3 right-3 text-[#888680] hover:text-[#d9fb06] transition-colors p-2"
          data-testid="quote-close-btn"
        >
          <X size={22} />
        </button>

        <div className="p-6 md:p-8">
          {success ? (
            <div className="text-center py-6" data-testid="quote-success">
              <div className="text-5xl mb-4">✅</div>
              <h3 className="text-2xl font-bold text-[#d9fb06] mb-3 uppercase">
                Demande envoyée
              </h3>
              <p className="text-[#dfddd6] mb-6">
                Merci ! Nous revenons vers vous très vite avec un devis personnalisé pour le service&nbsp;
                <strong className="text-[#d9fb06]">{service.label}</strong>.
              </p>
              <Button
                onClick={onClose}
                className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#c4e205] font-bold rounded-full px-8 py-3 uppercase"
              >
                Fermer
              </Button>
            </div>
          ) : (
            <>
              <p className="text-[#d9fb06] text-sm font-semibold uppercase mb-1">
                Demande de devis
              </p>
              <h3 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase mb-2">
                {service.label}
              </h3>
              <p className="text-[#888680] text-sm mb-6">
                Renseignez quelques infos, je reviens vers vous avec un devis sur-mesure sous 24-48&nbsp;h.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* honeypot */}
                <input type="text" name="website" value={form.website} onChange={handleChange}
                  className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <div>
                  <label className="block text-[#dfddd6] text-sm mb-1">
                    Nom <span className="text-[#d9fb06]">*</span>
                  </label>
                  <input
                    name="nom"
                    type="text"
                    required
                    value={form.nom}
                    onChange={handleChange}
                    className="w-full bg-[#26282a] border border-[#3f4816]/60 rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                    data-testid="quote-input-nom"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#dfddd6] text-sm mb-1">
                      Email <span className="text-[#d9fb06]">*</span>
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      className="w-full bg-[#26282a] border border-[#3f4816]/60 rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                      data-testid="quote-input-email"
                    />
                  </div>
                  <div>
                    <label className="block text-[#dfddd6] text-sm mb-1">Téléphone</label>
                    <input
                      name="telephone"
                      type="tel"
                      value={form.telephone}
                      onChange={handleChange}
                      className="w-full bg-[#26282a] border border-[#3f4816]/60 rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                      data-testid="quote-input-tel"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#dfddd6] text-sm mb-1">Type de bateau</label>
                    <input
                      name="type_bateau"
                      type="text"
                      placeholder="Voilier, vedette, catamaran…"
                      value={form.type_bateau}
                      onChange={handleChange}
                      className="w-full bg-[#26282a] border border-[#3f4816]/60 rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                      data-testid="quote-input-bateau"
                    />
                  </div>
                  <div>
                    <label className="block text-[#dfddd6] text-sm mb-1">Zone / Port</label>
                    <input
                      name="zone"
                      type="text"
                      placeholder="La Rochelle, Île de Ré…"
                      value={form.zone}
                      onChange={handleChange}
                      className="w-full bg-[#26282a] border border-[#3f4816]/60 rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                      data-testid="quote-input-zone"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[#dfddd6] text-sm mb-1">Votre besoin (optionnel)</label>
                  <textarea
                    name="message"
                    rows={3}
                    value={form.message}
                    onChange={handleChange}
                    className="w-full bg-[#26282a] border border-[#3f4816]/60 rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none resize-none"
                    data-testid="quote-input-message"
                  />
                </div>

                {error && (
                  <p className="text-red-400 text-sm" data-testid="quote-error">
                    {error}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#c4e205] font-bold rounded-full py-3 uppercase disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="quote-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Envoi en cours…
                    </>
                  ) : (
                    'Demander mon devis'
                  )}
                </Button>
                <p className="text-[#888680] text-xs text-center">
                  Vos données sont uniquement utilisées pour vous répondre — pas de spam.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteRequestModal;
